/**
 * Living plans — merging a user's update note into an EXISTING action plan.
 *
 * The invariant this module exists to enforce: COMPLETED STEPS ARE HISTORY.
 * An AI revision may reshape the remaining open steps freely (keep, drop,
 * rewrite, add, reorder), but a step the user has ticked off is a record of
 * work done and is preserved verbatim — id, text, minutes, done — no matter
 * what the provider returns. The provider is never even asked to rewrite
 * completed steps; this merge additionally drops any revised entry that
 * echoes one, so the guarantee holds even against a misbehaving model.
 */

import type { ActionStep } from '../types';
import { parseStepInput } from './manualSteps';

export interface RevisedStep {
  text: string;
  minutes: number;
}

/** Ceiling on plan length after a revision — plans are roadmaps, not backlogs. */
export const MAX_PLAN_STEPS = 12;

/** Most new steps a local fallback revision will graft from one note. */
export const MAX_FALLBACK_ADDITIONS = 5;

/** Case/whitespace-insensitive identity for step texts. */
function stepKey(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

function normalizeRevised(r: RevisedStep): RevisedStep | null {
  const text = r.text?.replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const minutes =
    typeof r.minutes === 'number' && Number.isFinite(r.minutes)
      ? Math.max(1, Math.min(240, Math.round(r.minutes)))
      : 10;
  return { text, minutes };
}

/**
 * Merge a revised open-step list into the existing plan.
 *
 * - Completed steps come through untouched, in their original relative
 *   order, ahead of the open work (they read as the plan's history).
 * - Revised entries that duplicate a completed step are dropped — a model
 *   echoing history back can never resurrect or duplicate it.
 * - An open step whose text survives the revision keeps its id (stable
 *   React keys; a step the user is mid-editing doesn't change identity).
 * - An empty or all-garbage revision returns the plan UNCHANGED: an update
 *   can reshape a plan but never wipe it.
 */
export function applyPlanRevision(
  existing: ActionStep[],
  revised: RevisedStep[],
  mkId: () => string,
): ActionStep[] {
  const done = existing.filter((s) => s.done);
  const doneKeys = new Set(done.map((s) => stepKey(s.text)));

  const usable = revised
    .map(normalizeRevised)
    .filter((r): r is RevisedStep => r !== null)
    .filter((r) => !doneKeys.has(stepKey(r.text)))
    .slice(0, Math.max(0, MAX_PLAN_STEPS - done.length));
  if (usable.length === 0) return existing;

  // Reuse ids where an open step's text survives the revision (first match wins).
  const idPool = new Map<string, string[]>();
  for (const s of existing) {
    if (s.done) continue;
    const k = stepKey(s.text);
    idPool.set(k, [...(idPool.get(k) ?? []), s.id]);
  }
  const open = usable.map((r) => {
    const ids = idPool.get(stepKey(r.text));
    const reused = ids?.shift();
    return { id: reused ?? mkId(), text: r.text, minutes: r.minutes, done: false };
  });

  return [...done, ...open];
}

/**
 * Offline/error fallback: keep every open step and graft new ones parsed
 * from the note (split on newlines / semicolons / sentence ends; trailing
 * "15 min" durations honored via parseStepInput). Deterministic, additive,
 * and honest — it never pretends to have understood the note's intent.
 */
export function fallbackRevision(open: ActionStep[], note: string): RevisedStep[] {
  const out: RevisedStep[] = open.map((s) => ({ text: s.text, minutes: s.minutes }));
  const seen = new Set(out.map((s) => stepKey(s.text)));
  let added = 0;
  for (const fragment of note.split(/[\n;.]+/)) {
    if (added >= MAX_FALLBACK_ADDITIONS) break;
    const trimmed = fragment.trim();
    if (trimmed.length < 3) continue;
    const parsed = parseStepInput(trimmed);
    if (!parsed) continue;
    const k = stepKey(parsed.text);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(parsed);
    added += 1;
  }
  return out;
}
